import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  X, ArrowRight, ArrowLeft, CreditCard, ShieldCheck, 
  TrendingUp, Video, Phone, CheckCircle, Target, Zap,
  Rocket, DollarSign, Clock, BarChart3, Settings, AlertCircle
} from 'lucide-react';
import CheckoutModal from '../components/landing/CheckoutModal';
import AfterPaymentBlock from '../components/catalog/AfterPaymentBlock';
import { usePrices } from '../context/PricesContext';

// Problems
const problems = [
  'Репрайсер кажется слишком сложным',
  'Много правил, условий, исключений',
  'Непонятно, что реально влияет на продажи',
  'Страх «сломать цену» и уйти в минус',
];

// What repricer allows
const repricerBenefits = [
  { icon: Target, text: 'Забирать Buy Box, снижая цену на центы' },
  { icon: TrendingUp, text: 'Поднимать цену, когда конкуренты уходят' },
  { icon: Clock, text: 'Повышать цену ночью / в низкий сезон' },
  { icon: Zap, text: 'Активнее продавать в часы пик' },
  { icon: Settings, text: 'Контролировать сотни SKU без ручной работы' },
];

// What you get
const courseContent = [
  {
    icon: Video,
    title: '1 подробный видео-урок',
    desc: 'Настройка репрайсера под вашу модель торговли (EU или USA)',
  },
  {
    icon: Phone,
    title: '1 личный созвон с Вадимом',
    desc: 'Разбор ваших настроек и стратегий',
  },
];

// What you learn
const learnings = [
  'Настроите репрайсер под свою модель торговли',
  'Поймёте, какие параметры влияют на Buy Box и маржу',
  'Получите готовые шаблоны настроек',
  'Разберёте стратегии по времени суток и дням недели',
  'Получите примеры кастомных стратегий через код',
];

// For whom
const forWhom = [
  'Уже продаёте на Amazon',
  'Используете репрайсер, но не понимаете его логику',
  'Хотите больше Buy Box без снижения маржи',
  'Масштабируете ассортимент',
];

// Not for
const notFor = [
  'Вы ещё не начали продавать',
  'У вас нет активных SKU',
  'Ищете «волшебную кнопку» без понимания процессов',
];

// Results
const results = [
  'Понимаете, как репрайсер принимает решения',
  'Контролируете цену, а не реагируете на конкурентов',
  'Используете репрайсер как инструмент прибыли',
  'Снижаете зависимость от демпинга на рынке',
];

const RepricerPage = () => {
  const [checkoutData, setCheckoutData] = useState({ isOpen: false, packageId: '', packageName: '', price: 0 });
  const { getPrice } = usePrices();
  const price = getPrice('repricer');

  const handleCheckout = () => {
    setCheckoutData({ 
      isOpen: true, 
      packageId: 'repricer', 
      packageName: 'Репрайсер — Настройка под прибыль', 
      price: price 
    });
  };

  const closeCheckout = () => {
    setCheckoutData({ isOpen: false, packageId: '', packageName: '', price: 0 });
  };

  return (
    <div className="min-h-screen bg-[#050506]">
      {/* Checkout Modal */}
      <CheckoutModal 
        isOpen={checkoutData.isOpen}
        onClose={closeCheckout}
        packageId={checkoutData.packageId}
        packageName={checkoutData.packageName}
        price={checkoutData.price}
        discountMode={false}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl blur opacity-40 group-hover:opacity-70 transition-opacity" />
                <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                  <span className="text-black font-black text-lg">A</span>
                </div>
              </div>
              <div>
                <span className="text-white font-bold text-lg group-hover:text-amber-400 transition-colors">Amazon</span>
                <span className="text-zinc-400 font-medium text-lg ml-1">Wholesale</span>
              </div>
            </Link>
            
            <Link 
              to="/catalog"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">В каталог</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Product Card */}
          <div className="relative mb-16">
            {/* Glow */}
            <div className="absolute -inset-3 bg-gradient-to-r from-violet-500/20 via-purple-500/30 to-violet-500/20 rounded-[2rem] blur-2xl" />
            
            {/* Card */}
            <div className="relative bg-gradient-to-b from-zinc-900 to-zinc-950 rounded-[1.5rem] border border-violet-500/30 overflow-hidden">
              
              {/* Header */}
              <div className="relative p-8 sm:p-10 bg-gradient-to-b from-violet-500/10 to-transparent border-b border-violet-500/20">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="flex items-center gap-5">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-xl shadow-violet-500/30">
                      <BarChart3 className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white">Репрайсер</h1>
                      <p className="text-violet-300 text-lg sm:text-xl mt-1">Настройка под прибыль, а не «авто-режим»</p>
                    </div>
                  </div>
                  <div className="text-left lg:text-right">
                    <p className="text-5xl sm:text-6xl font-black text-violet-400">${price}</p>
                    <p className="text-zinc-500 text-sm mt-1">разовый платёж</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 sm:p-10 space-y-10">
                
                {/* What is it */}
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                      <span className="text-violet-400 font-bold text-xl">?</span>
                    </span>
                    Что это?
                  </h3>
                  <p className="text-zinc-300 text-lg sm:text-xl leading-relaxed">
                    <span className="text-white font-semibold">Репрайсер</span> — один из самых мощных и недооценённых инструментов на Amazon.
                    Большинство продавцов используют его на <span className="text-red-400">5–10%</span> возможностей, теряя Buy Box, маржу и контроль над ценой.
                  </p>
                </div>

                {/* Problem */}
                <div className="p-6 sm:p-8 rounded-2xl bg-red-500/5 border border-red-500/20">
                  <h3 className="text-xl sm:text-2xl font-bold text-red-400 mb-4">
                    В чём проблема большинства продавцов
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {problems.map((problem, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-red-500/5">
                        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                        <span className="text-zinc-400 text-sm">{problem}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 text-zinc-500 text-sm">
                    В итоге: репрайсер либо выключен, либо работает по базовым настройкам, либо демпит и убивает маржу.
                  </p>
                </div>

                {/* Important */}
                <div className="p-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                  <h4 className="text-emerald-400 font-bold text-lg mb-4">
                    Важный момент
                  </h4>
                  <p className="text-zinc-300 text-base mb-4">
                    Репрайсер — это <span className="text-red-400 font-semibold">не</span> инструмент демпинга. 
                    При правильной настройке он позволяет:
                  </p>
                  <div className="space-y-3">
                    {repricerBenefits.map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-4 h-4 text-emerald-400" />
                        </div>
                        <span className="text-zinc-300 text-sm">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What you get */}
                <div className="p-6 sm:p-8 rounded-2xl bg-violet-500/5 border border-violet-500/20">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <Video className="w-6 h-6 text-violet-400" />
                    Что вы получите
                  </h3>
                  <div className="space-y-4">
                    {courseContent.map((item, i) => (
                      <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-bold text-lg">{item.title}</p>
                          <p className="text-zinc-400 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What you'll learn */}
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-5">
                    За это время вы:
                  </h3>
                  <div className="space-y-3">
                    {learnings.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/50">
                        <CheckCircle className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300 text-sm sm:text-base">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* For / Not For */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-5 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                    <h4 className="text-emerald-400 font-bold mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Подойдёт, если вы:
                    </h4>
                    <div className="space-y-2">
                      {forWhom.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-zinc-300 text-sm">
                          <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-5 rounded-xl bg-red-500/5 border border-red-500/20">
                    <h4 className="text-red-400 font-bold mb-4 flex items-center gap-2">
                      <X className="w-5 h-5" />
                      Не подойдёт, если:
                    </h4>
                    <div className="space-y-2">
                      {notFor.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-zinc-400 text-sm">
                          <X className="w-4 h-4 text-red-400 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-violet-500/10 to-violet-900/10 border border-violet-500/30">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                    <Rocket className="w-7 h-7 text-violet-400" />
                    РЕЗУЛЬТАТ
                  </h3>
                  <p className="text-zinc-400 text-base mb-4">После урока и созвона вы:</p>
                  <div className="space-y-3">
                    {results.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/50">
                        <CheckCircle className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300 text-sm sm:text-base">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Question */}
                <div className="text-center p-6 rounded-xl bg-amber-500/10 border border-amber-500/30">
                  <p className="text-amber-300 text-lg sm:text-xl font-medium">
                    Сколько денег вы теряете каждый месяц,<br />
                    используя репрайсер на <span className="text-red-400">10%</span> его возможностей?
                  </p>
                </div>

                {/* After Payment Block */}
                <AfterPaymentBlock />

                {/* CTA Section */}
                <div className="text-center space-y-6 pt-8 border-t border-zinc-800">
                  {/* Price Block */}
                  <div className="inline-block p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-violet-500/10 to-violet-900/20 border border-violet-500/30">
                    <p className="text-5xl sm:text-6xl font-black text-violet-400 mb-4">${price}</p>
                    
                    {/* Buy Button */}
                    <button
                      onClick={handleCheckout}
                      data-testid="repricer-buy-btn"
                      className="w-full flex items-center justify-center gap-3 px-10 py-5 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-black text-xl rounded-2xl hover:shadow-2xl hover:shadow-violet-500/30 hover:-translate-y-1 transition-all duration-300 mb-4"
                    >
                      <CreditCard className="w-6 h-6" />
                      <span>Купить курс</span>
                      <ArrowRight className="w-6 h-6" />
                    </button>
                    
                    <p className="text-zinc-500 text-sm">Разовый платёж • Доступ к видео навсегда</p>
                  </div>
                  
                  {/* Guarantee */}
                  <div className="flex items-center justify-center gap-2 text-zinc-500 text-base">
                    <ShieldCheck className="w-5 h-5 text-emerald-400" />
                    <span>Безопасная оплата через Stripe</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Back to catalog */}
          <div className="text-center">
            <Link 
              to="/catalog"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Вернуться в каталог</span>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RepricerPage;
